import React from "react";
import { Link } from "wouter";
import { SEOHead } from "@/components/SEOHead";
import { Header, Footer } from "@/components/Navigation";
import { PRACTICE_INFO } from "@/site-data";
import {
  Calendar,
  Phone,
  ShieldCheck,
  CheckCircle,
  ArrowRight,
  Star,
  Activity,
  Award,
  Zap,
  MapPin,
  Clock,
  Sparkles,
  Stethoscope
} from "lucide-react";

export default function Home() {
  const homeSchema = {
    "@context": "https://schema.org",
    "@type": ["MedicalClinic", "LocalBusiness"],
    name: "Wasatch Foot & Ankle Center",
    alternateName: "Dr. Spence Harper Podiatry",
    url: "https://www.drspenceharper.com/",
    logo: "https://www.drspenceharper.com/manus-storage/dr-spence-harper_029434bc.png",
    image: "https://www.drspenceharper.com/manus-storage/reception-main_cb5cd9eb.webp",
    telephone: "435-657-0329",
    address: {
      "@type": "PostalAddress",
      streetAddress: "190 N. Main Street",
      addressLocality: "Heber City",
      addressRegion: "UT",
      postalCode: "84032",
      addressCountry: "US"
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 40.5097,
      longitude: -111.4133
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Thursday"],
        opens: "08:00",
        closes: "17:00"
      }
    ],
    medicalSpecialty: "Podiatric",
    physician: {
      "@type": "Physician",
      name: "Spence D. Harper, DPM",
      jobTitle: "Board-Certified Podiatric Physician & Surgeon",
      medicalSpecialty: "Foot and Ankle Surgery"
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead
        title="Wasatch Foot & Ankle Center | Dr. Spence Harper, DPM | Heber City, UT"
        description="Locally owned podiatry care by board-certified Dr. Spence Harper in Heber City, UT. New patients and second opinions welcome for Swift microwave wart therapy, bunion care, heel pain, and foot surgery."
        canonicalPath="/"
        schema={homeSchema}
      />
      <Header />

      <main className="flex-1">
        {/* HERO SECTION WITH THE ORIGINAL MOUNTAIN BACKGROUND VIDEO */}
        <section className="relative min-h-[580px] lg:min-h-[640px] flex items-center justify-center overflow-hidden bg-[#1e3a5f]">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover opacity-85"
          >
            <source src={PRACTICE_INFO.heroVideo} type="video/mp4" />
          </video>

          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2b48]/82 via-[#0f2b48]/46 to-[#0b2138]/20" />

          {/* Hero Content Container */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 space-y-6 text-white text-left">
                {/* Board Certification Badge */}
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0369a1]/80 border border-cyan-300/40 text-cyan-100 text-xs sm:text-sm font-semibold tracking-wide shadow-sm backdrop-blur-md">
                  <ShieldCheck className="w-4 h-4 text-cyan-200" />
                  Board Certified &bull; American Board of Foot and Ankle Surgery
                </div>

                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight drop-shadow-md">
                  Expert Foot &amp; Ankle Relief in Heber City
                </h1>

                <p className="text-base sm:text-lg text-slate-100 max-w-2xl font-body leading-relaxed drop-shadow-sm">
                  As a <strong className="font-semibold text-white">locally owned</strong> Heber City practice, Wasatch Foot &amp; Ankle Center provides advanced surgical and conservative care led by board-certified podiatric surgeon <span className="font-semibold text-white">Dr. Spence D. Harper, DPM</span>. New patients and those seeking a <strong className="font-semibold text-white">second opinion</strong> are welcome for Swift microwave wart treatment, ingrown toenail care, bunion correction, fracture care, and personalized heel pain support.
                </p>

                {/* Primary & Secondary CTAs */}
                <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <Link
                    href="/appointments/"
                    className="inline-flex items-center justify-center gap-2.5 bg-[#0284c7] hover:bg-[#0369a1] text-white text-base font-bold px-7 py-3.5 rounded-lg shadow-xl hover:shadow-cyan-500/30 transition-all transform active:scale-95 border border-cyan-400/30"
                  >
                    <Calendar className="w-5 h-5" />
                    Request Appointment
                  </Link>

                  <a
                    href={`tel:${PRACTICE_INFO.phone}`}
                    className="inline-flex items-center justify-center gap-2.5 bg-white/15 hover:bg-white/25 text-white border border-white/40 text-base font-semibold px-6 py-3.5 rounded-lg backdrop-blur-md transition-all shadow-md"
                  >
                    <Phone className="w-5 h-5 text-cyan-200" />
                    Call: (435) 657-0329
                  </a>
                </div>

                {/* Trust Points */}
                <div className="pt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs sm:text-sm text-slate-100">
                  <div className="flex items-center gap-2 drop-shadow-sm">
                    <CheckCircle className="w-4 h-4 text-cyan-300 shrink-0" />
                    <span>25+ Years Experience</span>
                  </div>
                  <div className="flex items-center gap-2 drop-shadow-sm">
                    <CheckCircle className="w-4 h-4 text-cyan-300 shrink-0" />
                    <span>FDA-Cleared Swift Tech</span>
                  </div>
                  <div className="flex items-center gap-2 drop-shadow-sm">
                    <CheckCircle className="w-4 h-4 text-cyan-300 shrink-0" />
                    <span>Major Insurance Accepted</span>
                  </div>
                </div>
              </div>

              {/* Practice Highlights Card (Doctor Photo Removed per Request) */}
              <div className="lg:col-span-5 flex justify-center">
                <div className="w-full max-w-md bg-white/95 backdrop-blur-md rounded-3xl p-7 shadow-2xl border border-slate-200/80 text-slate-800 space-y-6">
                  <div className="border-b border-slate-100 pb-4 space-y-1">
                    <span className="text-xs uppercase font-bold tracking-widest text-[#0284c7]">
                      Wasatch Foot &amp; Ankle Center
                    </span>
                    <h2 className="font-heading font-bold text-2xl text-[#0f2b48]">
                      Specialized Foot &amp; Ankle Care
                    </h2>
                    <p className="text-xs text-slate-600 font-body">
                      Independent solo practice providing unhurried, direct physician care in Heber City.
                    </p>
                  </div>

                  <div className="space-y-3.5 text-xs sm:text-sm text-slate-700 font-body">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-cyan-100 text-[#0284c7] flex items-center justify-center shrink-0 mt-0.5">
                        <Zap className="w-4 h-4" />
                      </div>
                      <div>
                        <strong className="text-slate-900 block text-sm">Swift Microwave Wart Therapy</strong>
                        <span className="text-slate-600 text-xs">Brief in-office applications; a short burst of heat may be felt.</span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-cyan-100 text-[#0284c7] flex items-center justify-center shrink-0 mt-0.5">
                        <Activity className="w-4 h-4" />
                      </div>
                      <div>
                        <strong className="text-slate-900 block text-sm">Advanced Reconstructive Surgery</strong>
                        <span className="text-slate-600 text-xs">Board-certified surgical precision for bunions, spurs, and trauma.</span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-cyan-100 text-[#0284c7] flex items-center justify-center shrink-0 mt-0.5">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div>
                        <strong className="text-slate-900 block text-sm">Heel Pain &amp; Plantar Fasciitis Care</strong>
                        <span className="text-slate-600 text-xs">Evaluation, stretching, footwear guidance, and orthotic support.</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-100 flex flex-col gap-2.5">
                    <Link
                      href="/appointments/"
                      className="w-full text-center bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold py-3 px-4 rounded-xl shadow-md transition-all text-sm"
                    >
                      Request Consultation Online
                    </Link>
                    <Link
                      href="/about/"
                      className="w-full text-center text-xs font-bold text-[#0f2b48] hover:text-[#0284c7] py-1.5 transition-colors"
                    >
                      Learn More About Dr. Spence Harper, DPM &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* QUICK CONTACT STRIP */}
        <section className="bg-slate-50 border-b border-slate-200 py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-slate-800">
              <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center text-[#0284c7] shrink-0">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase font-bold tracking-wider text-slate-500">Practice Location</div>
                  <div className="font-bold text-sm text-[#0f2b48]">190 N. Main Street</div>
                  <div className="text-xs text-slate-600">Heber City, UT 84032</div>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center text-[#0284c7] shrink-0">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase font-bold tracking-wider text-slate-500">Clinic Hours</div>
                  <div className="font-bold text-sm text-[#0f2b48]">Mon, Tue, Thu: 8am – 5pm</div>
                  <div className="text-xs text-slate-600">Wed: Surgery · Fri: Scheduled procedures</div>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center text-[#0284c7] shrink-0">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase font-bold tracking-wider text-slate-500">Speak With Our Staff</div>
                  <div className="font-bold text-sm text-[#0f2b48]">
                    <a href={`tel:${PRACTICE_INFO.phone}`} className="hover:text-[#0284c7]">
                      (435) 657-0329
                    </a>
                  </div>
                  <div className="text-xs text-slate-600">Direct booking &amp; questions</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SWIFT MICROWAVE THERAPY SPOTLIGHT (CRITICAL EMPHASIS) */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#1e3a5f] rounded-3xl overflow-hidden shadow-2xl text-white">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center p-8 sm:p-12 lg:p-16">
                <div className="lg:col-span-7 space-y-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-200 text-xs font-semibold tracking-wider uppercase border border-cyan-400/40">
                    <Sparkles className="w-3.5 h-3.5" /> Breakthrough Plantar Wart Treatment
                  </div>
                  <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
                    Swift Microwave Therapy for Stubborn Plantar Warts
                  </h2>
                  <p className="text-slate-100 text-base leading-relaxed">
                    Tired of persistent plantar warts, painful freezing, or messy acid kits? Wasatch Foot &amp; Ankle Center offers <strong className="text-white">Swift Microwave Therapy</strong> — an FDA-cleared treatment that delivers brief, targeted microwave energy to the lesion and may help stimulate the body's immune response.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4" />
                      </div>
                      <div className="text-sm">
                        <div className="font-bold text-white">No Incisions or Dressings</div>
                        <div className="text-slate-200 text-xs">Sock-off, sock-on procedure with zero skin breakage.</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4" />
                      </div>
                      <div className="text-sm">
                        <div className="font-bold text-white">5–10 Minute Visits</div>
                        <div className="text-slate-200 text-xs">Rapid sessions spaced 4 weeks apart to fit your schedule.</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4" />
                      </div>
                      <div className="text-sm">
                        <div className="font-bold text-white">Zero Post-Visit Downtime</div>
                        <div className="text-slate-200 text-xs">Walk, exercise, and wear normal shoes immediately.</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4" />
                      </div>
                      <div className="text-sm">
                        <div className="font-bold text-white">Personalized Treatment Plan</div>
                        <div className="text-slate-200 text-xs">Dr. Harper reviews options based on the lesion and your goals.</div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 flex flex-col sm:flex-row gap-4">
                    <Link
                      href="/services/swift-microwave-therapy/"
                      className="inline-flex items-center justify-center gap-2 bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold px-6 py-3 rounded-lg shadow-md transition-all"
                    >
                      Explore Swift Wart Therapy Details <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      href="/appointments/"
                      className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/10 text-white border border-white/30 font-semibold px-6 py-3 rounded-lg transition-all"
                    >
                      Book Wart Consultation
                    </Link>
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-cyan-400/20">
                    <img
                      src={PRACTICE_INFO.swiftBannerPhoto}
                      alt="Official Swift microwave therapy device and treatment banner"
                      loading="lazy"
                      className="w-full aspect-video object-contain bg-white"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f2b48]/80 via-transparent to-transparent flex items-end p-6">
                      <div className="text-xs text-white/90">
                        <span className="font-bold text-cyan-300">FDA-Cleared Technology:</span> Advanced podiatric microwave therapy applied in Heber City, UT.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CORE SPECIALTIES & CONDITIONS (EMPHASIS FROM SURVEY) */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-14 space-y-4">
              <span className="text-[#0284c7] font-bold text-xs uppercase tracking-widest">
                Comprehensive Podiatric Care
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0f2b48]">
                Conditions We Treat &amp; Specialized Services
              </h2>
              <p className="text-slate-600 font-body text-base leading-relaxed">
                Whether you are contending with acute pain, an active sports injury, or chronic deformities, Dr. Harper provides individualized treatment plans combining conservative therapies and surgical excellence.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {PRACTICE_INFO.services.map((svc) => (
                <div
                  key={svc.id}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 p-7 flex flex-col justify-between group"
                >
                  <div className="space-y-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 group-hover:bg-cyan-100 flex items-center justify-center text-[#0f2b48] group-hover:text-[#0284c7] transition-colors">
                      {svc.id === "swift-therapy" && <Zap className="w-6 h-6" />}
                      {svc.id === "surgical-corrections" && <Activity className="w-6 h-6" />}
                      {svc.id === "bunion-correction" && <Award className="w-6 h-6" />}
                      {svc.id === "heel-pain-plantar-fasciitis" && <Sparkles className="w-6 h-6" />}
                      {svc.id === "ingrown-toenails" && <ShieldCheck className="w-6 h-6" />}
                      {svc.id === "neuromas" && <CheckCircle className="w-6 h-6" />}
                    </div>

                    <h3 className="font-heading font-bold text-xl text-[#0f2b48] group-hover:text-[#0284c7] transition-colors">
                      {svc.title}
                    </h3>

                    <p className="text-sm text-slate-600 leading-relaxed font-body">
                      {svc.shortDesc}
                    </p>

                    <div className="pt-2 border-t border-slate-100">
                      <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                        Common Symptoms:
                      </div>
                      <ul className="text-xs text-slate-600 space-y-1">
                        {svc.symptoms.slice(0, 2).map((s, idx) => (
                          <li key={idx} className="flex items-start gap-1.5">
                            <span className="text-[#0284c7] font-bold">&bull;</span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="pt-6 mt-6 border-t border-slate-100 flex items-center justify-between">
                    <Link
                      href={`/services/${svc.slug}/`}
                      className="text-xs font-bold text-[#0f2b48] group-hover:text-[#0284c7] inline-flex items-center gap-1 transition-colors"
                    >
                      Learn more about {svc.title} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    <Link
                      href="/appointments/"
                      className="text-xs font-semibold text-slate-500 hover:text-slate-800"
                    >
                      Book Visit
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                href="/services/"
                className="inline-flex items-center gap-2 bg-[#0f2b48] hover:bg-[#1e3a5f] text-white text-sm font-bold px-8 py-3.5 rounded-lg shadow transition-all"
              >
                View Complete Podiatry Services <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* PRACTICE RECEPTION & VALLEY PHOTO TOUR */}
        <section className="py-16 bg-slate-50 border-t border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-6 space-y-5">
                <span className="text-[#0284c7] font-bold text-xs uppercase tracking-widest">
                  Welcome to Wasatch Foot &amp; Ankle Center
                </span>
                <h2 className="font-heading text-3xl font-bold text-[#0f2b48]">
                  Personalized, Empathic Care in a Welcoming Solo Practice
                </h2>
                <p className="text-slate-600 font-body text-base leading-relaxed">
                  Unlike large corporate health systems where patients see a different provider every visit, Dr. Spence Harper treats each patient directly. We believe in taking ample time to listen, thoroughly explain clinical diagnoses, and partner with you on a comfortable recovery plan.
                </p>
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle className="w-5 h-5 text-emerald-700 shrink-0" />
                    <span>Independent, solo practice with no corporate quotas</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle className="w-5 h-5 text-emerald-700 shrink-0" />
                    <span>Dedicated surgery days on Wednesdays for focused operative care</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle className="w-5 h-5 text-emerald-700 shrink-0" />
                    <span>Convenient ground-floor parking on North Main Street</span>
                  </div>
                </div>

                <div className="pt-4">
                  <Link
                    href="/heber-office/"
                    className="inline-flex items-center gap-2 bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold text-sm px-6 py-3 rounded-lg shadow transition-all"
                  >
                    Office Details &amp; Directions <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden shadow-md border border-slate-200">
                  <img
                    src={PRACTICE_INFO.receptionPhoto}
                    alt="Wasatch Foot & Ankle Center Reception and Front Desk"
                    loading="lazy"
                    className="w-full aspect-[4/3] object-contain bg-[#eef4f4]"
                  />
                  <div className="p-3 bg-white text-xs font-semibold text-slate-700 text-center">
                    Heber City Office Reception
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden shadow-md border border-slate-200">
                  <img
                    src={PRACTICE_INFO.heberValleyPhoto}
                    alt="Heber Valley Utah Wasatch Mountains"
                    loading="lazy"
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-3 bg-white text-xs font-semibold text-slate-700 text-center">
                    Scenic Heber Valley Setting
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* VERIFIED PATIENT REVIEWS */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
              <span className="text-[#0284c7] font-bold text-xs uppercase tracking-widest">
                Patient Stories
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0f2b48]">
                What Our Patients Are Saying
              </h2>
              <div className="flex items-center justify-center gap-1 text-amber-500">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-current" />
                ))}
              </div>
              <p className="text-xs text-slate-500">
                Patient feedback from Wasatch County
              </p>
              <a
                href="https://search.google.com/local/writereview?placeid=ChIJWfBvY7P1TYcR1cbImu6-QFU"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-bold text-[#0284c7] hover:text-[#0369a1]"
              >
                <Star className="w-4 h-4 fill-current" /> Rate your visit on Google <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {PRACTICE_INFO.testimonials.slice(0, 3).map((item, idx) => (
                <div
                  key={idx}
                  className="bg-slate-50 rounded-2xl p-7 border border-slate-200/80 shadow-sm flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center gap-1 text-amber-500 mb-4">
                      {[...Array(item.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-current" />
                      ))}
                    </div>
                    <blockquote className="text-slate-700 italic text-sm leading-relaxed mb-6 font-body">
                      "{item.quote}"
                    </blockquote>
                  </div>
                  <div className="border-t border-slate-200 pt-4 flex items-center justify-between text-xs">
                    <span className="font-bold text-[#0f2b48]">{item.author}</span>
                    <span className="text-slate-500">{item.location}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ACCEPTED INSURANCE PROVIDERS */}
        <section className="py-12 bg-slate-100 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
            <h3 className="font-heading text-xl font-bold text-[#0f2b48]">
              We Accept Most Insurance Plans
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 max-w-2xl mx-auto leading-relaxed">
              <strong className="font-extrabold text-[#0f2b48]">We accept most insurances.</strong> The providers below are some of the plans we commonly work with; if your insurance is not on this list, please <a href={`tel:${PRACTICE_INFO.phone}`} className="font-bold text-[#0284c7] underline decoration-2 underline-offset-2 hover:text-[#0369a1]">call our office</a> and we will help verify coverage.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {PRACTICE_INFO.insurances.map((ins, idx) => (
                <div
                  key={idx}
                  className="bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm text-xs sm:text-sm font-semibold text-slate-700"
                >
                  {ins}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CALL TO ACTION BOTTOM BANNER */}
        <section className="bg-[#1e3a5f] text-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center space-y-6">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold leading-tight">
              Ready to Walk, Run, and Live Without Foot Pain?
            </h2>
            <p className="text-slate-200 text-base max-w-2xl mx-auto font-body">
              Schedule your consultation with Dr. Spence Harper today. We make appointment requests simple, responsive, and stress-free.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Link
                href="/appointments/"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold px-8 py-3.5 rounded-lg shadow-lg transition-all"
              >
                <Calendar className="w-5 h-5" /> Request Appointment Online
              </Link>
              <a
                href={`tel:${PRACTICE_INFO.phone}`}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 font-semibold px-8 py-3.5 rounded-lg transition-all"
              >
                <Phone className="w-5 h-5 text-cyan-200" /> Call (435) 657-0329
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
